import { Badge, Button, Divider, Flex } from 'antd';
import { AppstoreOutlined, BlockOutlined, BorderOutlined, CloseOutlined, MinusOutlined } from '@ant-design/icons';
import { comboList, type HotkeyActionId } from '@/shared/lib/hotkeys';
import { HotkeyTip } from '@/shared/ui/hotkey';
import { TASKBAR_Z_INDEX } from './zLayers';

export interface TaskbarItem {
  key: string;
  label: string;
  /** Есть ли в этом свёрнутом окне несохранённые изменения — точка на плашке, не разворачивая. */
  dirty: boolean;
  /** Номер окна среди всех открытых (по порядку открытия): `Ctrl+Alt+<номер>` выводит его вперёд. */
  number?: number;
}

/**
 * Панель управления окнами слева внизу (как панель задач в Windows).
 *
 * «Свернуть все»/«Каскадом»/«Сеткой» относятся к открытым (не свёрнутым) окнам и видны, пока
 * хотя бы одно такое есть. «Закрыть все» и плашки — к свёрнутым: клик по плашке разворачивает
 * окно обратно на то же место и в том же размере (окно не размонтировалось); «Закрыть все»
 * закрывает их все разом, но через обычное закрытие каждого окна (с проверкой несохранённых
 * изменений, см. FloatingWindow) — а не в обход неё.
 */
export function WindowTaskbar({
  items,
  hasOpenWindows,
  onRestore,
  onCloseAll,
  onMinimizeAll,
  onCascadeAll,
  onTileAll,
}: {
  items: TaskbarItem[];
  hasOpenWindows: boolean;
  onRestore: (key: string) => void;
  onCloseAll: () => void;
  onMinimizeAll: () => void;
  onCascadeAll: () => void;
  onTileAll: () => void;
}) {
  if (!hasOpenWindows && items.length === 0) return null;
  return (
    <Flex
      data-testid="window-taskbar"
      align="center"
      gap={4}
      wrap
      justify="flex-start"
      style={{ position: 'fixed', left: 8, bottom: 8, maxWidth: 'calc(100vw - 16px)', zIndex: TASKBAR_Z_INDEX }}
    >
      {hasOpenWindows ? (
        <>
          <HotkeyTip catalogId="app.minimizeAll" label="Свернуть все окна" display="inline-flex">
            <Button size="small" icon={<MinusOutlined />} aria-label="Свернуть все окна" onClick={onMinimizeAll} style={BUTTON_STYLE}>
              Свернуть все
            </Button>
          </HotkeyTip>
          <HotkeyTip catalogId="app.cascade" label="Разложить окна каскадом" display="inline-flex">
            <Button size="small" icon={<BlockOutlined />} aria-label="Разложить окна каскадом" onClick={onCascadeAll} style={BUTTON_STYLE} />
          </HotkeyTip>
          <HotkeyTip catalogId="app.tile" label="Разложить окна сеткой" display="inline-flex">
            <Button size="small" icon={<AppstoreOutlined />} aria-label="Разложить окна сеткой" onClick={onTileAll} style={BUTTON_STYLE} />
          </HotkeyTip>
        </>
      ) : null}

      {hasOpenWindows && items.length > 0 ? <Divider type="vertical" style={{ margin: 0 }} /> : null}

      {items.length > 0 ? (
        <>
          <HotkeyTip catalogId="app.closeMinimized" label="Закрыть все свёрнутые окна" display="inline-flex">
            <Button size="small" icon={<CloseOutlined />} aria-label="Закрыть все свёрнутые окна" onClick={onCloseAll} style={BUTTON_STYLE}>
              Закрыть все
            </Button>
          </HotkeyTip>
          <Divider type="vertical" style={{ margin: 0 }} />
          {items.map(({ key, label, dirty, number }) => (
            <HotkeyTip
              key={key}
              label={`Развернуть: ${label}${dirty ? ' (есть несохранённые изменения)' : ''}`}
              combo={number !== undefined && number >= 1 && number <= 9 ? comboList(`app.taskbar${number}` as HotkeyActionId) : undefined}
              display="inline-flex"
            >
              <Button size="small" icon={<BorderOutlined />} onClick={() => onRestore(key)} style={{ ...BUTTON_STYLE, maxWidth: 240 }}>
                <Badge dot={dirty} offset={[2, 0]}>
                  <span style={{ display: 'inline-block', maxWidth: 190, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom' }}>
                    {label}
                  </span>
                </Badge>
              </Button>
            </HotkeyTip>
          ))}
        </>
      ) : null}
    </Flex>
  );
}

const BUTTON_STYLE: React.CSSProperties = { fontSize: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.25)' };
